import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Longer-term goals in a gymnast's diary: separate from the daily entries.
// The gymnast sets a goal and a target date, adds notes as they go, and marks
// it completed or cancelled. Everyone else who can read the diary sees them.

const DAY_MS = 24 * 60 * 60 * 1000;
const STATUS_LABELS = { COMPLETED: 'Completed', CANCELLED: 'Cancelled' };

const formatDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function timeLeft(iso, today = new Date()) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((new Date(`${iso}T00:00:00`) - start) / DAY_MS);
  if (days < 0) return 'target date passed';
  if (days === 0) return 'today';
  if (days < 14) return `${days} day${days === 1 ? '' : 's'} to go`;
  return `${Math.floor(days / 7)} weeks to go`;
}

function GoalForm({ goal, onSave, onCancel }) {
  const [title, setTitle] = useState(goal?.title || '');
  const [targetDate, setTargetDate] = useState(goal?.targetDate || '');
  const [notes, setNotes] = useState(goal?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Write your goal first.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), targetDate: targetDate || null, notes });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the goal.');
      setSaving(false);
    }
  };

  return (
    <form className="diary-goal-form" onSubmit={submit} aria-label={goal ? 'Edit goal' : 'New goal'}>
      <label className="diary-field">Goal
        <input className="form-control" value={title} maxLength={200} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Land a full twisting back, or compete at Regional 3" />
      </label>
      <label className="diary-field diary-field--short">Target date (optional)
        <input type="date" className="form-control" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
      </label>
      <label className="diary-field">Notes
        <textarea className="form-control" rows={3} value={notes} maxLength={2000} onChange={e => setNotes(e.target.value)}
          placeholder="How it's going, what to work on next" />
      </label>
      {error && <p className="bk-error">{error}</p>}
      <div className="diary-goal__actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving...' : 'Save goal'}</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

export default function DiaryGoals({ gymnastId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null, 'new' or a goal id
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!gymnastId) return;
    axios.get(`/api/training-diary/gymnasts/${gymnastId}/goals`)
      .then(res => setData({ goals: res.data.goals || [], canWrite: Boolean(res.data.canWrite) }))
      .catch(() => setError('Could not load goals.'));
  }, [gymnastId]);

  useEffect(() => { setData(null); setEditing(null); load(); }, [load]);

  const save = async (fields) => {
    if (editing === 'new') await axios.post(`/api/training-diary/gymnasts/${gymnastId}/goals`, fields);
    else await axios.put(`/api/training-diary/goals/${editing}`, fields);
    setEditing(null);
    load();
  };

  const setStatus = async (goal, status) => {
    if (status === 'CANCELLED' && !window.confirm(`Cancel the goal "${goal.title}"?`)) return;
    try {
      await axios.put(`/api/training-diary/goals/${goal.id}`, { status });
      load();
    } catch {
      setError('Could not update the goal.');
    }
  };

  if (error) return <div className="card diary-goals"><p className="bk-error">{error}</p></div>;
  if (!data) return null;
  const { goals, canWrite } = data;
  const active = goals.filter(g => g.status === 'ACTIVE');
  const finished = goals.filter(g => g.status !== 'ACTIVE');
  if (!canWrite && goals.length === 0) return null;

  return (
    <section className="card diary-goals" aria-labelledby="diary-goals-title">
      <div className="diary-goals__head">
        <h4 className="card-title" id="diary-goals-title">Goals</h4>
        {canWrite && editing !== 'new' && (
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing('new')}>Set a goal</button>
        )}
      </div>
      {editing === 'new' && <GoalForm onSave={save} onCancel={() => setEditing(null)} />}
      {active.length === 0 && editing !== 'new' && (
        <p className="diary-empty">
          {canWrite ? 'Set a longer-term goal, like a new skill or a competition, and a date to aim for.' : 'No goals being worked on.'}
        </p>
      )}
      <ul className="diary-goals__list">
        {active.map(goal => (
          <li key={goal.id} className="diary-goal">
            {editing === goal.id ? <GoalForm goal={goal} onSave={save} onCancel={() => setEditing(null)} /> : (
              <>
                <div className="diary-goal__title">{goal.title}</div>
                {goal.targetDate && (
                  <div className="diary-goal__date">By {formatDate(goal.targetDate)} · {timeLeft(goal.targetDate)}</div>
                )}
                {goal.notes && <p className="diary-goal__notes">{goal.notes}</p>}
                {canWrite && (
                  <div className="diary-goal__actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setStatus(goal, 'COMPLETED')}>Completed</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(goal.id)}>Edit or add notes</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setStatus(goal, 'CANCELLED')}>Cancel goal</button>
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {finished.length > 0 && (
        <details className="diary-goals__finished">
          <summary>Finished goals ({finished.length})</summary>
          <ul className="diary-goals__list">
            {finished.map(goal => (
              <li key={goal.id} className={`diary-goal diary-goal--${goal.status.toLowerCase()}`}>
                <div className="diary-goal__title">
                  <span className={`diary-goal__badge diary-goal__badge--${goal.status.toLowerCase()}`}>{STATUS_LABELS[goal.status]}</span>
                  {goal.title}
                </div>
                <div className="diary-goal__date">
                  {goal.closedAt && `${STATUS_LABELS[goal.status]} ${formatDate(goal.closedAt.slice(0, 10))}`}
                  {goal.targetDate && ` · target was ${formatDate(goal.targetDate)}`}
                </div>
                {goal.notes && <p className="diary-goal__notes">{goal.notes}</p>}
                {canWrite && (
                  <div className="diary-goal__actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setStatus(goal, 'ACTIVE')}>Reopen</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
